import { Alert, AlertTitle, Button } from "@mui/material";
import { FlowButtons } from "components/NewOrders/NewOrders.styles";
import TrucksHeader from "components/Trucks/TrucksHeader/TrucksHeader";
import {
	AddOrderDraftsToTruckText,
	AddOrdersText,
	AddOrderToTruckErrorText,
	AddStopText,
	AddStopToTruckErrorText,
	CantFindOrderInTruckText,
	CantProcessRequestText,
	CheckYourSearchSpellingText,
	PleaseContactSalesRepText,
	RequestDateErrorHelperDescription,
	RequestTruckDateFormError,
	SaveAndContinueButton,
	SavingTruckErrorText,
	ServerTemporarilyUnavailableText,
	SomethingDontLookRightText,
	ToSeeOrderOnListText,
	TruckCubeCountErrorText,
	TruckErrorBannerSubheader,
	TruckSnackbarLoadingText,
	TruckSnackbarSuccessText,
	UniqueTruckErrorText
} from "constants/text";
import { useNavigate } from "react-router";
import TruckSubheader from "components/Trucks/TruckSubheader/TruckSubheader";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { models } from "types/api/viewModels";
import TruckStopOrdersCard from "components/Trucks/TruckStopOrdersCard/TruckStopOrdersCard";
import {
	AddOrdersPageContentWrapper,
	AddOrdersPageErrorBannerWrapper,
	NoOrdersContainer,
	TruckInfoOrdersContainer
} from "./AddOrdersPage.styles";
import AddIcon from "@mui/icons-material/Add";
import { SyntheticEvent, useMemo, useState } from "react";
import { useSaveTruckMutation } from "features/api/truckApi";
import { ContactCare, NewOrderBack } from "components/NewOrders/constants";
import Snackbar from "components/Common/SnackBar/SnackBar";
import useSnackbarEffect from "hooks/useSnackbarEffect";
import ReviewTruckDetails, {
	ReviewTruckDetailsErrorTypes
} from "components/Trucks/components/ReviewTruckDetails/ReviewTruckDetails";
import { truckActions } from "features/reducers/truck/truck.ts";
import { combinedJobNameAndPO } from "utils/order";
import SaveDraftNoDraftsFilter, {
	SaveDraftTemplateModalDetailsInterface
} from "components/NewOrders/SaveDraft/SaveDraftNoDraftsFilter";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";

const AddOrdersPage = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const truck = useSelector((state: RootState) => state.truck.truck);
	const truckStops = truck?.stops;
	const allTruckOrders = useMemo(() => truckStops?.flatMap((stops) => stops.draftOrders), [truckStops]);
	const allTruckOrdersLineItems = useMemo(
		() => allTruckOrders?.flatMap((order) => order?.configurations?.flatMap((config) => config.lineItems)),
		[allTruckOrders]
	);

	const eachStopHasOrders = truckStops?.every(
		(stop: models["TruckStopViewModel"]) => stop.draftOrderIds && stop.draftOrderIds.length > 0
	);
	const cubeCountExceedsCapacity = useMemo(
		() =>
			(allTruckOrdersLineItems
				?.flatMap((item) => item?.cubeCount ?? 0)
				.reduce((total: number, num) => total + (num ?? 0), 0) ?? 0) > (truck?.cubeCapacity ?? 0),
		[allTruckOrdersLineItems, truck?.cubeCapacity]
	);

	const { saveTruckErrorMessage, setSaveTruckErrorMessage, openDialog } = useDialog();
	const [errorStatus, setErrorStatus] = useState<string>();
	const [isManuallySaving, setIsManuallySaving] = useState(false);
	const [isEditingDetails, setIsEditingDetails] = useState(false);

	type ErrorType = "truckMissingStops" | "stopsMissingOrders" | "apiError" | ReviewTruckDetailsErrorTypes;
	const [errorTypes, setErrorTypes] = useState<ErrorType[]>();

	const uniqueErrorFilter = (value: ErrorType, index: number, array: ErrorType[]) =>
		array.findIndex((innerValue) => value === innerValue) === index;

	const setErrorTypesUnique = (value: ErrorType) => {
		setErrorTypes((prev) => {
			const next: typeof prev = [...(prev ?? []), value];
			return next.filter(uniqueErrorFilter);
		});
	};

	const removeErrorType = (value: ErrorType) => {
		setErrorTypes((prev) => {
			const next: typeof prev = [...(prev ?? [])];
			return next.filter((innerValue) => innerValue !== value);
		});
	};

	const hasError = (value: ErrorType) =>
		errorTypes && errorTypes?.findIndex((innerValue) => value === innerValue) > -1;

	const hasValidationError = () =>
		hasError("invalidDate") ||
		hasError("stopsMissingOrders") ||
		hasError("truckMissingStops") ||
		hasError("truckNameAlreadyExists");

	const hasAnyError = () => hasValidationError() || hasError("apiError");

	const [saveTruck, { isLoading: isSavingTruck, error }] = useSaveTruckMutation();
	const { snackbarOpen } = useSnackbarEffect(isSavingTruck && isManuallySaving, saveTruckErrorMessage);

	const [searchValue, setSearchValue] = useState<string | null>(null);

	const handleContinue = async () => {
		let hasError = hasAnyError();

		if (cubeCountExceedsCapacity) {
			window.scrollTo(0, 0);
			hasError = true;
		}

		setIsManuallySaving(false);

		if (!truck) {
			setErrorTypesUnique("apiError");
			window.scrollTo(0, 0);
			hasError = true;
		}

		if (truckStops && truckStops.length < 1) {
			setErrorTypesUnique("truckMissingStops");
			window.scrollTo(0, 0);
			hasError = true;
		} else {
			removeErrorType("truckMissingStops");
		}

		if (!eachStopHasOrders) {
			setErrorTypesUnique("stopsMissingOrders");
			window.scrollTo(0, 0);
			hasError = true;
		} else {
			removeErrorType("stopsMissingOrders");
		}

		if (hasError) {
			return;
		}

		try {
			await saveTruck(truck!)
				.unwrap()
				.then(async (response) => {
					if (response && eachStopHasOrders) {
						dispatch(truckActions.updateTruck(response));
						navigate("/trucks/review");
					} else {
						throw Error();
					}
				});
		} catch (error: any) {
			console.error("Save truck failed:", error);
			setErrorStatus(error?.status);
			setErrorTypes(["apiError"]);
			window.scrollTo(0, 0);
		}
	};

	const handleSaveTruck = async () => {
		setIsManuallySaving(true);
		if (!truck) {
			setErrorTypes(["apiError"]);
			setSaveTruckErrorMessage(SomethingDontLookRightText);
			window.scrollTo(0, 0);
			return;
		}
		try {
			setSaveTruckErrorMessage(undefined);
			setErrorStatus(undefined);
			await saveTruck(truck).unwrap();
		} catch (error: any) {
			console.error("Save truck failed:", error);
			setErrorTypes(["apiError"]);
			setSaveTruckErrorMessage(ServerTemporarilyUnavailableText);
			setErrorStatus(error?.status);
			window.scrollTo(0, 0);
		}
	};

	const handleBack = () => {
		navigate("/trucks/create-truck");
	};

	const handleEditStateChange = (editing: boolean) => {
		setIsEditingDetails(editing);
	};

	const searchOptions: string[] | undefined = useMemo(
		() => truckStops?.flatMap((stop) => stop.draftOrders ?? []).map((order) => combinedJobNameAndPO(order)),
		[truckStops]
	);

	const handleDraftSearch = (_event: SyntheticEvent, values: string | null) => {
		setSearchValue(values ?? null);
	};

	const filteredTruckStops = useMemo(
		() =>
			searchValue
				? truckStops?.filter((stop) =>
						stop.draftOrders?.some((order) =>
							combinedJobNameAndPO(order).toLowerCase().includes(searchValue.toLowerCase())
						)
					)
				: truckStops,
		[searchValue, truckStops]
	);

	const SaveDraftModalDetails: SaveDraftTemplateModalDetailsInterface = {
		NoSaveFoundHeader: CantFindOrderInTruckText,
		NoSaveListSubheader: ToSeeOrderOnListText,
		NoSaveFoundFirstBullet: CheckYourSearchSpellingText,
		NoSaveFoundSecondBullet: AddOrderDraftsToTruckText,
		NoSaveListFooter: PleaseContactSalesRepText
	};

	const isDisplayingErrorMessage = errorTypes && errorTypes.length > 0;

	const noSearchResults = searchValue && searchValue !== "" && filteredTruckStops && filteredTruckStops.length === 0;
	return (
		<div data-testid="trucks-add-orders-page">
			<TrucksHeader isLoading={isSavingTruck} />
			<AddOrdersPageContentWrapper>
				{(isDisplayingErrorMessage || cubeCountExceedsCapacity || error) && !isSavingTruck && (
					<AddOrdersPageErrorBannerWrapper>
						<Alert severity="error">
							<AlertTitle>{CantProcessRequestText}</AlertTitle>
							{!hasError("apiError") && <div>{TruckErrorBannerSubheader}</div>}
							{hasError("apiError") && (
								<div data-testid="error-banner-message">{ServerTemporarilyUnavailableText}</div>
							)}
							<ul>
								{cubeCountExceedsCapacity && <li>{TruckCubeCountErrorText}</li>}
								{hasValidationError() && (
									<>
										{hasError("stopsMissingOrders") && <li>{AddOrderToTruckErrorText}</li>}
										{hasError("truckMissingStops") && <li>{AddStopToTruckErrorText}</li>}
										{hasError("truckNameAlreadyExists") && <li>{UniqueTruckErrorText}</li>}
										{hasError("invalidDate") && (
											<li>
												{RequestDateErrorHelperDescription} {RequestTruckDateFormError}
											</li>
										)}
									</>
								)}
							</ul>
							{!hasError("apiError") && <span>{ContactCare}</span>}
							{errorStatus && <span> ({errorStatus} Error)</span>}
						</Alert>
					</AddOrdersPageErrorBannerWrapper>
				)}
				{truck && !isSavingTruck && (
					<ReviewTruckDetails
						editable
						onEditStateChange={handleEditStateChange}
						onError={(errors) => {
							for (const error of errors) {
								error.type && setErrorTypesUnique(error.type);
							}
						}}
						onClearError={(errors) => {
							if (errors) {
								for (const error of errors) {
									removeErrorType(error);
								}
							} else {
								removeErrorType("truckNameAlreadyExists");
								removeErrorType("invalidDate");
							}
						}}
					/>
				)}
				{!isSavingTruck && (
					<TruckSubheader
						title={AddOrdersText}
						showSaveButton={!isEditingDetails}
						handleSaveTruck={handleSaveTruck}
						searchOptions={searchOptions}
						handleDraftSearch={handleDraftSearch}
						searchValue={searchValue}
					/>
				)}
				<TruckInfoOrdersContainer>
					{filteredTruckStops &&
						filteredTruckStops.length > 0 &&
						filteredTruckStops?.map((stop: models["TruckStopViewModel"]) => (
							<TruckStopOrdersCard
								key={stop.truckStopId}
								stop={stop}
								index={truckStops?.findIndex((truckStop) => truckStop === stop) ?? 0}
								isLoading={isSavingTruck}
								searchValue={searchValue}
							/>
						))}
					{noSearchResults && (
						<NoOrdersContainer>
							<SaveDraftNoDraftsFilter SaveDraftTemplateModalDetails={SaveDraftModalDetails} />
						</NoOrdersContainer>
					)}
					{!isSavingTruck && !noSearchResults && (
						<Button
							onClick={() => {
								openDialog({ dialogStatus: DialogTracking.ADD_TRUCK_STOP });
							}}
							variant="text"
							startIcon={
								<AddIcon
									fontSize="small"
									sx={{ marginBottom: "2px" }}
								/>
							}
							data-id="truck-add-stop-button"
						>
							{AddStopText}
						</Button>
					)}
				</TruckInfoOrdersContainer>
				{!isSavingTruck && !isEditingDetails && (
					<FlowButtons>
						<Button
							data-testid="trucks-add-orders-back-button"
							variant="text"
							onClick={handleBack}
						>
							{NewOrderBack}
						</Button>
						<Button
							data-testid="trucks-add-orders-continue-button"
							onClick={handleContinue}
							variant="contained"
						>
							{SaveAndContinueButton}
						</Button>
					</FlowButtons>
				)}
				<Snackbar
					open={snackbarOpen}
					isLoading={isSavingTruck}
					loadingText={TruckSnackbarLoadingText}
					successText={TruckSnackbarSuccessText}
					isError={saveTruckErrorMessage !== undefined}
					errorText={SavingTruckErrorText}
				/>
			</AddOrdersPageContentWrapper>
		</div>
	);
};

export default AddOrdersPage;
